import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFlags, useRefreshFlags } from '../api/hooks'
import { createNadDebug } from '../utils/debugLog'
import FlagRow from '../components/admin/FlagRow'
import AdminToolNav from '../components/admin/AdminToolNav'
import { parseFlagError } from '../components/admin/flagErrors'

// NAD trace for the flag-admin surface (feature=flags-admin).
const flagsAdminLog = createNadDebug('dw:flags-admin:operator-ui')

// Admin/operator surface (gated by AdminFlagsRoute + the backend broad-admin or
// flags_admin capability, RFC-0856 I7). Lists registered feature flags and lets
// an operator set their runtime values via the /admin/flags API.
export default function AdminFlagsPage() {
  const { t } = useTranslation('pages')
  const { data: flags, error: flagsError, isLoading, isError, isFetching, refetch } = useFlags()
  const refresh = useRefreshFlags()
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null)
  const [confirmingRefresh, setConfirmingRefresh] = useState(false)

  const rows = flags ?? []
  const loaded = !isLoading && !isError
  const loadErrorMessage =
    flagsError instanceof Error ? parseFlagError(flagsError).message : t('adminFlags.loadError')
  useEffect(() => {
    if (loaded) {
      flagsAdminLog({
        event: 'flags-admin:list_loaded',
        feature: 'flags-admin',
        concern: 'ui',
        count: rows.length,
      })
    }
  }, [loaded, rows.length])

  const doRefresh = async () => {
    setConfirmingRefresh(false)
    setRefreshMsg(null)
    flagsAdminLog({
      event: 'flags-admin:refresh_invoked',
      feature: 'flags-admin',
      concern: 'ui',
    })
    try {
      await refresh.mutateAsync()
      setRefreshMsg(t('adminFlags.refreshDone'))
    } catch (err) {
      setRefreshMsg(err instanceof Error ? err.message : t('adminFlags.refreshFailed'))
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6" data-testid="admin-flags-page">
      <AdminToolNav />
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground mb-1">{t('adminFlags.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('adminFlags.description')}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {confirmingRefresh ? (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground/60">{t('adminFlags.refreshConfirm')}</span>
              <button
                onClick={() => setConfirmingRefresh(false)}
                className="px-2 py-1 text-xs font-medium text-foreground/70 hover:text-foreground cursor-pointer"
              >
                {t('adminFlags.confirmCancel')}
              </button>
              <button
                onClick={() => void doRefresh()}
                disabled={refresh.isPending}
                className="h-7 cursor-pointer rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 disabled:opacity-50"
                data-testid="flags-refresh-confirm"
              >
                {t('adminFlags.confirmYes')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingRefresh(true)}
              className="h-7 cursor-pointer rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3"
              data-testid="flags-refresh-button"
            >
              {t('adminFlags.refreshButton')}
            </button>
          )}
          {refreshMsg && (
            <span className="text-xs font-medium text-foreground/60" data-testid="flags-refresh-message">
              {refreshMsg}
            </span>
          )}
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">{t('adminFlags.loading')}</p>}

      {isError && (
        <div className="flex items-center gap-3 rounded-xl border border-destructive/40 bg-destructive/10 p-4">
          <p className="text-sm text-destructive" data-testid="flags-load-error">
            {loadErrorMessage}
          </p>
          <button
            onClick={() => void refetch()}
            className="ml-auto h-7 cursor-pointer rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3"
          >
            {t('adminFlags.retry')}
          </button>
        </div>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <div
          className="rounded-xl border border-border bg-surface-1 p-6 text-center"
          data-testid="flags-empty"
        >
          <p className="text-sm font-medium text-foreground/80">{t('adminFlags.emptyTitle')}</p>
          <p className="mt-1 text-xs font-medium text-muted-foreground">{t('adminFlags.emptyBody')}</p>
        </div>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface-1">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="border-b border-border-subtle text-left text-xs font-medium text-muted-foreground">
                <th className="px-4 py-3 font-medium">{t('adminFlags.colKey')}</th>
                <th className="px-4 py-3 font-medium">{t('adminFlags.colValue')}</th>
                <th className="px-4 py-3 font-medium">{t('adminFlags.colVersion')}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((flag) => (
                <FlagRow key={flag.key} flag={flag} listRefetching={isFetching} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
